/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ReactTagManager } from 'react-gtm-ts';
import { useTranslation } from 'react-i18next';
import { httpClient } from 'api';
import { Toast } from 'primereact/toast';
import { PageMissing } from 'components/PageMissing';
import { Button } from 'components/_new/Button';
import { MaintenanceNotification } from 'components/MaintenanceNotification';
import { useMaintenance } from 'hooks/useMaintenance';

// contexts
import { GlobalContextProvider } from 'contexts/globalContext';
import { AuthContextProvider } from 'contexts/authContext';
import { PanelContextProvider } from 'contexts/panelContext';

// auth
import { Verify } from 'components/auth/Verify';
import AuthContainer from 'components/auth/AuthContainer';
import Register from 'components/auth/Register';
import PasswordSetup from 'components/auth/PasswordSetup';
import Login from 'components/auth/Login';
import PasswordReset from 'components/auth/PasswordReset';
import EmailChangeConfirm from 'components/auth/EmailChangeConfirm';
import RegisterOrganization from 'components/auth/RegisterOrganization';
import Confirm from 'components/auth/Confirm';
import ExternalAuth from 'components/auth/ExternalAuth';

import RequireAuth from 'components/RequireAuth';
import ChangeOrganizationWrapper from 'components/_common/ChangeOrganizationWrapper';
import RequireTerms from 'components/auth/RequireTerms';
import { ScopeContextProvider } from 'contexts/scopeContext';

// landing page
import OrganizationLandingPage from 'components/landingPages/OrganizationLandingPage';
import ProgramLandingPage from 'components/landingPages/ProgramLandingPage';
import { Enter } from 'components/Enter';

// user panel
import UserPanelLayout from 'components/userPanel/UserPanelLayout';
import Wall from 'components/wall/Wall';
import Application from 'components/userPanel/Application';
// import FilesContainer from 'components/knowledgeDatabase/FilesContainer';
import LinkedInBadgePage from 'components/LinkedInBadgePage';
import Sessions from 'components/userPanel/sessions/Sessions';
import SessionsList from 'components/userPanel/sessions/SessionsList';
import SessionAgenda from 'components/userPanel/sessions/SessionAgenda';
import { Contract } from 'components/userPanel/contract/Contract';
import { FinalRaport } from 'components/userPanel/finalReport/FinalReport';
import ProcessSummary from 'components/userPanel/processSummary/ProcessSummary';
import { MentibotAI } from 'components/userPanel/mentibotAi/MentibotAI';
import { MentibotAIList } from 'components/userPanel/mentibotAi/MentibotAIList';
import { MentibotAIForm } from 'components/userPanel/mentibotAi/MentibotAIForm';
import { Browse } from 'components/userPanel/userFiles/Browse';
import { DispalyPage } from 'components/userPanel/userFiles/DisplayPage';
import { Contact } from 'components/userPanel/Contact';
import GoalsWrapper from 'components/userPanel/goals/Goals';

// program/admin panel
import OrganizationPanel from 'components/organizationPanel/OrganizationPanel';
import { OrganizationList } from 'components/organizationPanel/OrganizationList';
import { OrganizationGuard } from 'components/organizationPanel/OrganizationGuard';
import OrganizationMemberships from 'components/organizationPanel/OrganizationMemberships';
import OrganizationProgramList from 'components/organizationPanel/OrganizationProgramList';
import ProgramPanel from 'components/programPanel/ProgramPanel';
import ProgramDashboard from 'components/programPanel/programDashboard/dashboard/ProgramDashboard';
import ProgramPairList from 'components/programPanel/ProgramPairList';
import ProgramPairDetails from 'components/programPanel/ProgramPairDetails';
import ProgramApplicationList from 'components/programPanel/ProgramApplicationList';
import ProgramPairingDetails from 'components/programPanel/ProgramApplicationDetails';
import ProgramMembershipList from 'components/programPanel/ProgramMembershipList';
import ProgramMembership from 'components/programPanel/ProgramMembership';
import { ProgramPostList } from 'components/programPanel/ProgramPostList';
import { ProgramPostAdd } from 'components/programPanel/ProgramPostAdd';
import { ProgramPostEdit } from 'components/programPanel/ProgramPostEdit';
import { MyPersonalDetails } from 'components/userPanel/MyPersonalDetails';
import { PairProvider } from 'contexts/pairContext';
import { PairDetails } from 'components/userPanel/PairDetails'; // some update for any change

// other
import { ProgramStatisticsNew } from 'components/programPanel/programStatistics/ProgramStatisticsNew';
import { PairMembers } from 'components/userPanel/pairMembers/PairMembers';
import { FinalSurveyAccess } from 'components/finalSurveyPage/FinalSurveyAccess';
import { FinalSurveyPanel } from 'components/finalSurveyPage/FinalSurveyPanel';
import { ProgramLandingPageEditor } from 'components/programPanel/ProgramLandingPageEditor';
import ProgramConfig from 'components/programPanel/programDashboard/config/ProgramConfig';
import { PageLoading } from 'components/PageLoading';
import i18n from 'locale';
import Pairing from 'components/userPanel/pairing/Pairing';
import { ChatContainer, ScopeChatContainer } from 'components/chat/ChatContainer';
import { InstallPromptProvider } from 'components/_new/PwaManager/InstallPromptContext';
import { KnowledgeBasePage } from 'components/userPanel/KnowledgeBasePage';
import { ProgramKnowledgeBaseContainer } from 'components/programPanel/knowledgeBase/ProgramKnowledgeBase';
import { ProgramPostMentibotInfo } from 'components/programPanel/ProgramPostMentibotInfo';
import { CoordinatorPanel } from 'components/coordinatorPanel/CoordinatorPanel';
import { CoordinatorPairList } from 'components/coordinatorPanel/CoordinatorPairList';
import { CoordinatorPairDetails } from 'components/coordinatorPanel/CoordinatorPairDetails';
import { CoordinatorApplicationDetails } from 'components/coordinatorPanel/CoordinatorApplicationDetails';
import { CoordinatorKnowledgeBaseContainer } from 'components/coordinatorPanel/CoordinatorKnowledgeBase';
import { CoordinatorProgramDashboard } from 'components/coordinatorPanel/CoordinatorProgramDashboard';

import { TimelinePage } from 'components/userPanel/TimelinePage';
import { ProgramTimelineEventAdd } from 'components/programPanel/timeline/ProgramTimelineEventAdd';
import { ProgramTimelineEventEdit } from 'components/programPanel/timeline/ProgramTimelineEventEdit';
import { ProgramTimelineList } from 'components/programPanel/timeline/ProgramTimelineList';
import { ProgramTimelineEventDetails } from 'components/programPanel/timeline/ProgramTimelineEventDetails';
import ProgramPairing from 'components/programPanel/pairing/ProgramPairing';
import { SessionAgendaAddNote } from 'components/userPanel/sessions/SessionAgendaAddNote';
import { SessionLastRedirect } from 'components/userPanel/sessions/SessionLastRedirect';
import { VideoCallPage } from 'components/videocall/VideocallCockpit';
import { ProgramFeedback } from './components/programPanel/programFeedback/ProgramFeedback';
import EditApplicationQuestions from './components/programPanel/EditApplicationQuestions';
import EditContract from './components/programPanel/EditContract';
import { ProgramFeedbackEditQuestions } from './components/programPanel/programFeedback/ProgramFeedbackEditQuestions';

const isDevelopment = process.env.NODE_ENV === 'development';
const googleTagId = process.env.REACT_APP_GTM;
if (googleTagId) {
	ReactTagManager.init({
		code: googleTagId,
		debug: isDevelopment,
		performance: true,
	});
}

export interface RouterUrlParams<T = string> {
	[key: string]: T;
}

const App = () => {
	const toastRef = useRef<Toast>(null);
	const { scheduledDate, showNotification } = useMaintenance();

	const queryClient = new QueryClient({
		defaultOptions: {
			mutations: {
				retry: false,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Error types
				onError: (error: any) => {
					if (error.response?.data?.message) {
						toastRef.current?.show({
							severity: 'error',
							life: 3000,
							summary: 'Error Message',
							detail: error.response.data.message,
						});
					}
				},
			},
			queries: {
				retry: false,
				enabled: false,
				refetchOnWindowFocus: false,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Error types
				onError: (error: any) => {
					if (error.response?.data?.message) {
						toastRef.current?.show({
							severity: 'error',
							life: 3000,
							summary: 'Error Message',
							detail: error.response.data.message,
						});
					} else {
						console.error(error);
					}
				},
			},
		},
	});

	const [apiLoading, setApiLoading] = useState(true);
	const [apiCheckDisabled, setApiCheckDisabled] = useState(false);
	const [apiError, setApiError] = useState<any>(null);
	const { t } = useTranslation();
	const apiCheck = useCallback(() => {
		if (!apiCheckDisabled) {
			setApiCheckDisabled(true);
			setTimeout(() => {
				setApiCheckDisabled(false);
			}, 2500);

			setApiError(null);
			setApiLoading(true);
			httpClient
				.get<string>('status', undefined, {
					timeout: 15000,
				})
				.then((response: string) => {
					if (response === 'ok') {
						setApiLoading(false);
					}
				})
				.catch((err: any) => {
					setApiLoading(false);
					setApiError(err);
				});
		}
	}, [apiCheckDisabled]);

	useEffect(() => {
		apiCheck();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (apiError || apiLoading) {
		return (
			<div className="h-full w-full flex flex-1 justify-content-center align-items-center">
				{!apiError && apiLoading && <PageLoading text={i18n.t('start.applicationLoading')} />}
				{apiError && !apiLoading && (
					<div className="flex flex-column justify-content-center align-items-center">
						<h2>{`${i18n.t('start.serverNotResponding')}`}</h2>
						<Button onClick={apiCheck} label={i18n.t('start.tryAgain')} disabled={apiCheckDisabled} />
					</div>
				)}
			</div>
		);
	}

	return (
		<>
			<Helmet title={t('landingPages.helmetTitle')} titleTemplate={`%s | ${t('landingPages.helmetAppName')}`}>
				<meta name="description" content={t('landingPages.helmetDescription')} />
			</Helmet>

			{scheduledDate && showNotification && <MaintenanceNotification />}

			<QueryClientProvider client={queryClient}>
				<GlobalContextProvider
					value={{
						toastRef,
					}}
				>
					<InstallPromptProvider>
						<AuthContextProvider>
							<Toast ref={toastRef as RefObject<Toast>} position="bottom-right" />
							<Routes>
								<Route index element={<Enter />} />
								<Route element={<AuthContainer />}>
									<Route path="verify" element={<Verify />} />
									<Route path="login" element={<Login />} />
									<Route path="register-organization" element={<RegisterOrganization />} />
									<Route path="register" element={<Register />} />
									<Route path="confirm-organization/:token" element={<Confirm />} />
									<Route path="password-reset" element={<PasswordReset />} />
									<Route path="confirm/:token" element={<PasswordSetup mode="confirm" />} />
									<Route path="reset/:token" element={<PasswordSetup mode="reset" />} />
									<Route path="email-change/:token" element={<EmailChangeConfirm />} />
								</Route>
								<Route path="auth/:type" element={<ExternalAuth />} />

								<Route element={<RequireAuth strict />}>
									<Route path="change-organization" element={<ChangeOrganizationWrapper />} />
									<Route path="require-terms" element={<RequireTerms />} />
									<Route path="meeting/:sessionMeetingUuid" element={<VideoCallPage />} />
								</Route>

								<Route element={<ScopeContextProvider />}>
									<Route element={<RequireAuth strict />}>
										<Route
											path="panel"
											element={
												<ScopeChatContainer context="panel">
													<Outlet />
												</ScopeChatContainer>
											}
										>
											<Route index element={<Navigate to="/" />} />
											<Route path=":organizationName">
												<Route index element={<Navigate to="/" />} />
												<Route path=":programName" element={<PanelContextProvider />}>
													<Route
														path=":type/pair/:pairId/session/last/:subpage"
														element={<SessionLastRedirect />}
													/>
													<Route
														path=":type/pair/:pairId/session/:sessionId/note/:userFileId"
														element={<SessionAgendaAddNote />}
													/>
													<Route
														path=":type/pair/:pairId/session/:sessionId/note"
														element={<SessionAgendaAddNote />}
													/>

													<Route path=":type" element={<UserPanelLayout />}>
														<Route index element={<Wall />} />
														<Route
															path="personal-details"
															element={<MyPersonalDetails />}
														/>
														<Route path="application" element={<Application />} />
														<Route path="pairing" element={<Pairing />} />
														<Route path="browseMembers" element={<PairMembers />} />
														<Route
															path="knowledge-base/*"
															element={<KnowledgeBasePage />}
														/>
														<Route path="linkedin-badge" element={<LinkedInBadgePage />} />
														<Route path="pair/:pairId" element={<PairProvider />}>
															<Route index element={<Navigate to="contract" />} />
															<Route path="contract" element={<Contract />} />

															<Route path="session" element={<Sessions />}>
																<Route index element={<SessionsList />} />
																<Route
																	path=":sessionId/*"
																	element={<SessionAgenda />}
																/>
															</Route>

															<Route path="mentee-plan" element={<GoalsWrapper />} />
															<Route path="final-report" element={<FinalRaport />} />
															<Route path="final-survey" element={<FinalSurveyPanel />} />
															<Route
																path="process-summary"
																element={<ProcessSummary />}
															/>

															<Route path="mentibot-ai" element={<MentibotAI />}>
																<Route index element={<Navigate to="list" />} />
																<Route path="list" element={<MentibotAIList />} />
																<Route
																	path="form/questions"
																	element={<MentibotAIForm formType="questions" />}
																/>
																<Route
																	path="form/techniques"
																	element={<MentibotAIForm formType="techniques" />}
																/>
																<Route
																	path="form/smart"
																	element={<MentibotAIForm formType="smart" />}
																/>
																<Route
																	path="form/homework"
																	element={<MentibotAIForm formType="homework" />}
																/>
															</Route>
															<Route path="userfiles">
																<Route index element={<Browse />} />
																<Route
																	path="display/:userFileId"
																	element={<DispalyPage />}
																/>
															</Route>
															<Route path="pair-details" element={<PairDetails />} />
														</Route>
														<Route path="contact" element={<Contact />} />

														<Route path="timeline" element={<TimelinePage />} />
													</Route>
												</Route>
											</Route>
										</Route>
										<Route path="organization/*" element={<OrganizationPanel />}>
											<Route index element={<Navigate to="list" />} />
											<Route path="list" element={<OrganizationList />} />
											<Route path=":organizationName/*" element={<OrganizationGuard />}>
												<Route index element={<Navigate to="program" />} />
												{/* <Route path="edit" element={<OrganizationEdit />} /> */}
												{/* <Route path="add-program" element={<OrganziationProgramAdd />} /> */}
												<Route path="memberships" element={<OrganizationMemberships />} />
												<Route path="program/*">
													<Route index element={<Navigate to="list" />} />
													<Route path="list" element={<OrganizationProgramList />} />
													<Route path="edit/:id" />
												</Route>
											</Route>
										</Route>
										<Route
											path="program/:organizationName/:programName/*"
											element={
												<ScopeChatContainer context="program-admin">
													<ProgramPanel />
												</ScopeChatContainer>
											}
										>
											<Route index element={<Navigate to="dashboard" />} />
											<Route path="dashboard" element={<ProgramDashboard />} />
											<Route path="pairing" element={<ProgramPairing />} />
											<Route path="config" element={<ProgramConfig />} />
											<Route path="pair/*">
												<Route index element={<Navigate to="list" />} />
												<Route path="list" element={<ProgramPairList />} />
												<Route path=":pairId" element={<ProgramPairDetails />} />
											</Route>
											<Route path="application/*">
												<Route index element={<Navigate to="list" />} />
												<Route path="list" element={<ProgramApplicationList />} />
												<Route path=":applicationId" element={<ProgramPairingDetails />} />
											</Route>
											<Route path="membership/*">
												<Route index element={<Navigate to="list" />} />
												<Route path="list" element={<ProgramMembershipList />} />
												<Route path=":membershipId" element={<ProgramMembership />} />
											</Route>
											<Route path="communication/*">
												<Route index element={<Navigate to="list" />} />
												<Route path="list" element={<ProgramPostList />} />
												<Route path="add" element={<ProgramPostAdd />} />
												<Route path="edit/:id" element={<ProgramPostEdit />} />
												<Route path="mentibot" element={<ProgramPostMentibotInfo />} />
											</Route>
											<Route path="statistics/*" element={<ProgramStatisticsNew />} />
											<Route
												path="feedback/edit-questions"
												element={<ProgramFeedbackEditQuestions />}
											/>
											<Route path="feedback" element={<ProgramFeedback />} />
											<Route path="edit-questions" element={<EditApplicationQuestions />} />
											<Route path="contract" element={<EditContract />} />
											<Route
												path="knowledge-base/*"
												element={<ProgramKnowledgeBaseContainer />}
											/>
											<Route path="landing-page" element={<ProgramLandingPageEditor />} />
											<Route path="timeline">
												<Route index element={<Navigate to="list" />} />
												<Route path="list" element={<ProgramTimelineList />} />
												<Route path="add" element={<ProgramTimelineEventAdd />} />
												<Route
													path="details/:eventId"
													element={<ProgramTimelineEventDetails />}
												/>
												<Route path="edit/:eventId" element={<ProgramTimelineEventEdit />} />
											</Route>
										</Route>
									</Route>
									<Route
										path="coordinator/:organizationName/:programName/*"
										element={
											<ScopeChatContainer context="coordinator">
												<CoordinatorPanel />
											</ScopeChatContainer>
										}
									>
										<Route index element={<Navigate to="dashboard" />} />
										<Route path="dashboard" element={<CoordinatorProgramDashboard />} />
										<Route path="pair/*">
											<Route index element={<Navigate to="list" />} />
											<Route path="list" element={<CoordinatorPairList />} />
											<Route path=":pairId" element={<CoordinatorPairDetails />} />
										</Route>
										<Route path="application/*">
											<Route index element={<Navigate to="list" />} />
											<Route path=":applicationId" element={<CoordinatorApplicationDetails />} />
										</Route>
										<Route
											path="knowledge-base/*"
											element={<CoordinatorKnowledgeBaseContainer />}
										/>
									</Route>

									<Route
										element={
											<ScopeChatContainer context="panel">
												<Outlet />
											</ScopeChatContainer>
										}
									>
										<Route
											path=":organizationName/:programName/*"
											element={<ProgramLandingPage />}
										/>
										<Route path=":organizationName" element={<OrganizationLandingPage />} />
									</Route>
								</Route>
								<Route path="/final-survey/:uuid" element={<FinalSurveyAccess />} />
								<Route path="*" element={<PageMissing />} />
							</Routes>
							<ReactQueryDevtools initialIsOpen={false} />
						</AuthContextProvider>
					</InstallPromptProvider>
				</GlobalContextProvider>
			</QueryClientProvider>
		</>
	);
};

export default App;
